import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, PenTool, Search, Target, Zap,
  UsersRound, Heart, BarChart3, Map, Lock, CreditCard, Settings,
} from 'lucide-react';
import type { FC } from 'react';
import type { Course } from '@/lib/learning/courses';
import { LearningProgressBar } from './LearningProgressBar';

const ICON_MAP: Record<string, FC<{ className?: string }>> = {
  TrendingUp, PenTool, Search, Target, Zap,
  UsersRound, Heart, BarChart3, Map, CreditCard, Settings,
};

interface CourseCardProps {
  course: Course;
  completedCount: number;
}

export const CourseCard: FC<CourseCardProps> = ({ course, completedCount }) => {
  const navigate = useNavigate();
  const Icon = ICON_MAP[course.icon] ?? TrendingUp;
  const pct = course.lessonCount > 0 ? Math.round((completedCount / course.lessonCount) * 100) : 0;
  const isDone = pct === 100;

  return (
    <button
      onClick={() => course.available && navigate(`/learning/${course.slug}`)}
      disabled={!course.available}
      className={`group relative flex flex-col gap-3 rounded-xl border p-5 text-left transition-all duration-200 ${
        course.available
          ? 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-md cursor-pointer'
          : 'border-dashed border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 opacity-60 cursor-not-allowed'
      }`}
    >
      {/* Icon + Badge */}
      <div className="flex items-start justify-between">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${
          isDone
            ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
            : 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
        }`}>
          <Icon className="w-5 h-5" />
        </div>
        {!course.available && (
          <span className="flex items-center gap-1 rounded-md bg-slate-100 dark:bg-slate-700 px-2 py-0.5 text-[10px] font-medium text-slate-500 dark:text-slate-400">
            <Lock className="w-3 h-3" />
            Bientot
          </span>
        )}
        {isDone && (
          <span className="rounded-md bg-emerald-50 dark:bg-emerald-900/30 px-2 py-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
            Termine
          </span>
        )}
      </div>

      {/* Title + Subtitle */}
      <div>
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white leading-tight">
          {course.title}
        </h3>
        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
          {course.subtitle}
        </p>
      </div>

      {/* Progress */}
      {course.available && (
        <LearningProgressBar completed={completedCount} total={course.lessonCount} size="sm" />
      )}
    </button>
  );
};
